"use client";

import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight, HardHat, Building2, House } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhoWeWorkWith() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const tabData = [{
    id: "engineers",
    label: "Engineers",
    icon: <HardHat className="w-4 h-4" />,
    title: "Precision Data for Structural Integrity",
    description: "We partner with structural and civil engineers to provide the rigorous soil analysis required for safe, compliant designs. Our certified professionals deliver accurate, high-fidelity data that integrates seamlessly into your workflow, ensuring your projects stand on solid ground.",
    image: "https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/AutoCAD-support-engineering-and-architectural-Firms.jpg?v=b103xyub8hf",
    link: "/services"
  }, {
    id: "developers",
    label: "Developers",
    icon: <Building2 className="w-4 h-4" />,
    title: "Rapid Mobilization & Scalable Solutions",
    description: "Time is critical in development. We understand the pressure of tight schedules and offer flexible, rapid mobilization to keep your timeline moving. From initial site feasibility to final compliance reports, we provide the clarity you need to mitigate risk and accelerate construction.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1550713/img/developers_planning_meeting.png",
    link: "/contact"
  }, {
    id: "property-owners",
    label: "Property Owners",
    icon: <House className="w-4 h-4" />,
    title: "Protecting Your Investment",
    description: "Whether you are building a custom home or managing a commercial portfolio, understanding the ground beneath you is vital. We simplify the geotechnical process, providing clear, actionable insights that protect your investment from unforeseen foundation issues.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1550713/img/property_owners_consultation.png",
    link: "/contact"
  }];
  return <section id="who-we-work-with" ref={ref} className={cn("py-24 bg-background transition-opacity duration-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
      <div className="container mx-auto px-4 md:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-wide text-foreground mb-4">
            Who We Work With
          </h2>
          <p className="text-muted-foreground text-lg">
            Tailored geotechnical solutions for every stakeholder in the construction process.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <Tabs defaultValue="engineers" className="flex flex-col gap-8">
            <div className="flex justify-center">
              <TabsList className="bg-muted/50 p-1 rounded-full border border-border/50">
                {tabData.map((tab, i) => <TabsTrigger key={tab.id} value={tab.id} data-index={i} className="rounded-full px-6 py-2.5 text-sm md:text-base font-medium uppercase tracking-wide data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all">
                    <span className="hidden md:inline-block mr-2">{tab.icon}</span>
                    {tab.label}
                  </TabsTrigger>)}
              </TabsList>
            </div>

            {tabData.map((tab, i) => <TabsContent key={tab.id} value={tab.id} data-index={i} className="focus-visible:outline-none focus-visible:ring-0 mt-0">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-border shadow-xl bg-card">
                  {}
                  <div className="p-8 md:p-12 flex flex-col justify-center order-2 lg:order-1">
                    <div className="mb-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-6">
                        {React.cloneElement(tab.icon as React.ReactElement, {
                      className: "w-6 h-6"
                    })}
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-foreground uppercase mb-4">
                        {tab.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed text-lg mb-8">
                        {tab.description}
                      </p>
                      <Button asChild size="lg" className="rounded-full w-fit">
                        
                      </Button>
                    </div>
                  </div>

                  {}
                  <div className="relative h-64 lg:h-auto order-1 lg:order-2 overflow-hidden group">
                    <div className="absolute inset-0 bg-primary/10 z-10 mix-blend-multiply pointer-events-none" />
                    <img src={tab.image} alt={`${tab.label} context`} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-in-out scale-105 group-hover:scale-100" />
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}